import React, { useState } from 'react';

const BasketballRadarChart = ({seasonStats}) => {
  // Player statistics data

console.log('season_stats',seasonStats)
   const filteredStats = seasonStats
        .filter(stat => stat.Season === "2025")[0]


  const playerStats = [
    { metric: 'Efficiency', value: filteredStats?.Efficiency, max: 25, rank: 808 },
    { metric: 'ReboundsPerGame', value: filteredStats?.ReboundsPerGame, max: 12, rank: 1160 },
    { metric: 'AssistsPerGame', value: filteredStats?.AssistsPerGame, max: 8, rank: 1372 },
    { metric: 'FieldGoalPercentage', value: filteredStats?.FieldGoalPercentage, max: 70, rank: 1265 },
    { metric: 'FreeThrowPercentage', value: filteredStats?.FreeThrowPercentage, max: 100, rank: 613 },
    { metric: 'PointsPerGame', value: filteredStats?.PointsPerGame, max: 20, rank: 1355 }
  ];

  const [hoveredPoint, setHoveredPoint] = useState(null);

  // Chart dimensions
  const size = 550;
  const center = size / 2;
  const radius = 150;
  const levels = 5;

  // Calculate angles for each metric (starting from top, going clockwise)
  const angleStep = (2 * Math.PI) / playerStats.length;
  
  // Generate polygon points
  const generatePolygonPoints = (data) => {
    return data.map((stat, index) => {
      const angle = (index * angleStep) - (Math.PI / 2); // Start from top
      const normalizedValue = stat.value / stat.max;
      const distance = radius * normalizedValue;
      
      return {
        x: center + Math.cos(angle) * distance,
        y: center + Math.sin(angle) * distance,
        angle: angle,
        ...stat
      };
    });
  };

  // Generate grid lines (concentric polygons)
  const generateGridLines = () => {
    const gridLines = [];
    
    for (let level = 1; level <= levels; level++) {
      const levelRadius = (radius * level) / levels;
      const points = playerStats.map((_, index) => {
        const angle = (index * angleStep) - (Math.PI / 2);
        return {
          x: center + Math.cos(angle) * levelRadius,
          y: center + Math.sin(angle) * levelRadius
        };
      });
      
      gridLines.push(points);
    }
    
    return gridLines;
  };

  // Generate axis lines
  const generateAxisLines = () => {
    return playerStats.map((stat, index) => {
      const angle = (index * angleStep) - (Math.PI / 2);
      return {
        x1: center,
        y1: center,
        x2: center + Math.cos(angle) * radius,
        y2: center + Math.sin(angle) * radius,
        stat: stat
      };
    });
  };

  // Generate label positions
  const generateLabels = () => {
    return playerStats.map((stat, index) => {
      const angle = (index * angleStep) - (Math.PI / 2);
      const labelDistance = radius + 30;
      
      return {
        x: center + Math.cos(angle) * labelDistance,
        y: center + Math.sin(angle) * labelDistance,
        stat: stat,
        angle: angle
      };
    });
  };

  const polygonPoints = generatePolygonPoints(playerStats);
  const gridLines = generateGridLines();
  const axisLines = generateAxisLines();
  const labels = generateLabels();

  // Convert points to SVG path
  const polygonPath = polygonPoints.map((point, index) => 
    `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  ).join(' ') + ' Z';

  const gridPaths = gridLines.map(line => 
    line.map((point, index) => 
      `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
    ).join(' ') + ' Z'
  );

  // Get rank color based on performance
  const getRankColor = (rank) => {
    if (rank <= 500) return '#10b981'; // Green - excellent
    if (rank <= 1000) return '#f59e0b'; // Yellow - good
    if (rank <= 1500) return '#f97316'; // Orange - average
    return '#ef4444'; // Red - needs improvement
  };

  return (
    <div className="w-full mx-auto">


      <div className="flex flex-col justify-center lg:flex-row items-center gap-8">
        {/* Radar Chart */}
        <div className="relative">
          <svg width={size} height={size} className="bg-gray-50 rounded-lg shadow-inner">
            {/* Grid lines */}
            {gridPaths.map((path, index) => (
              <path
                key={`grid-${index}`}
                d={path}
                fill="none"
                stroke="#374151"
                strokeWidth="1"
                opacity="0.3"
              />
            ))}

            {/* Axis lines */}
            {axisLines.map((line, index) => (
              <line
                key={`axis-${index}`}
                x1={line.x1}
                y1={line.y1}
                x2={line.x2}
                y2={line.y2}
                stroke="#4b5563"
                strokeWidth="1"
                opacity="0.5"
              />
            ))}

            {/* Data polygon */}
            <path
              d={polygonPath}
              fill="rgba(59, 130, 246, 0.2)"
              stroke="#3b82f6"
              strokeWidth="2"
              className="drop-shadow-lg"
            />

            {/* Data points */}
            {polygonPoints.map((point, index) => (
              <circle
                key={`point-${index}`}
                cx={point.x}
                cy={point.y}
                r={hoveredPoint === index ? 8 : 6}
                fill={getRankColor(point.rank)}
                stroke="BLACK"
                strokeWidth="2"
                className="cursor-pointer transition-all duration-200 hover:r-8"
                onMouseEnter={() => setHoveredPoint(index)}
                onMouseLeave={() => setHoveredPoint(null)}
              />
            ))}

            {/* Labels */}
            {labels.map((label, index) => (
              <text
                key={`label-${index}`}
                x={label.x}
                y={label.y}
                textAnchor="middle"
                dominantBaseline="middle"
                className="text-xs font-medium text-black "
                style={{
                  textAnchor: label.angle > Math.PI/2 || label.angle < -Math.PI/2 ? 'end' : 'start'
                }}
              >
                {label.stat.metric}
              </text>
            ))}

            {/* Center dot */}
            <circle
              cx={center}
              cy={center}
              r="3"
              fill="#64748b"
            />
          </svg>

          {/* Hover tooltip */}
          {hoveredPoint !== null && (
            <div className="absolute top-4 left-4 bg-slate-700 text-white p-3 rounded-lg shadow-lg border border-slate-600">
              <h4 className="font-semibold text-sm">{polygonPoints[hoveredPoint].metric}</h4>
              <p className="text-lg font-bold text-blue-400">{polygonPoints[hoveredPoint].value}</p>
              <p className="text-xs text-slate-300">
                Rank: #{polygonPoints[hoveredPoint].rank} overall
              </p>
            </div>
          )}
        </div>

        {/* Legend and Stats */}
      
      </div>
    </div>
  );
};

export default BasketballRadarChart;